/**
 * Enhanced ConfirmModal Component
 *
 * This component handles the entire confirmation flow internally:
 * - Manages loading state during async operations
 * - Automatically closes on success (configurable)
 * - Provides success and error callbacks
 * - Prevents multiple submissions during loading
 * - Handles both sync and async onConfirm functions
 */

import { AlertTriangle, X } from 'lucide-react';
import React, { useState, useCallback } from 'react';

import Button from '@/shared/components/ui/Button';

import Modal from './Modal';

export interface IConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  showIcon?: boolean;
  autoCloseOnSuccess?: boolean;
  onSuccess?: () => void;
  onError?: (error: Error | unknown) => void;
  headerAlign?: 'left' | 'center';
  bodyAlign?: 'left' | 'center';
  buttonsAlign?: 'left' | 'right';
  showHeaderCloseButton?: boolean;
}

const ConfirmModal: React.FC<IConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed with this action?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  showIcon = true,
  autoCloseOnSuccess = true,
  onSuccess,
  onError,
  headerAlign = 'left',
  bodyAlign = 'left',
  buttonsAlign = 'right',
  showHeaderCloseButton = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm();
      if (autoCloseOnSuccess) {
        onClose();
      }
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, onConfirm, autoCloseOnSuccess, onClose, onSuccess, onError]);

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          icon: <AlertTriangle className='w-6 h-6 text-red-600' />,
          confirmButtonVariant: 'danger' as const,
          iconBg: 'bg-red-100',
        };
      case 'warning':
        return {
          icon: <AlertTriangle className='w-6 h-6 text-yellow-600' />,
          confirmButtonVariant: 'warning' as const,
          iconBg: 'bg-yellow-100',
        };
      case 'info':
        return {
          icon: <AlertTriangle className='w-6 h-6 text-blue-600' />,
          confirmButtonVariant: 'primary' as const,
          iconBg: 'bg-blue-100',
        };
      default:
        return {
          icon: <AlertTriangle className='w-6 h-6 text-red-600' />,
          confirmButtonVariant: 'danger' as const,
          iconBg: 'bg-red-100',
        };
    }
  };

  const variantStyles = getVariantStyles();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='sm'
      closeOnOverlayClick={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={false}
    >
      <div className='p-3'>
        <div
          className={
            headerAlign === 'center'
              ? 'grid grid-cols-[auto,1fr,auto] items-center gap-3'
              : `flex items-center ${showHeaderCloseButton ? 'justify-between' : 'justify-start'}`
          }
        >
          <div className='flex items-center gap-3'>
            {showIcon && (
              <div className={`flex-shrink-0 p-2 rounded-full ${variantStyles.iconBg}`}>
                {variantStyles.icon}
              </div>
            )}
            <h3
              className={`text-lg font-semibold text-gray-900 ${headerAlign === 'center' ? 'text-center' : ''}`}
            >
              {title}
            </h3>
          </div>

          {showHeaderCloseButton && (
            <button
              type='button'
              onClick={onClose}
              disabled={isLoading}
              className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50'
              aria-label='Close'
            >
              <X size={18} />
            </button>
          )}
        </div>

        <div className={`${bodyAlign === 'center' ? 'text-center' : ''} mt-3 mb-6`}>
          <div className='text-sm text-gray-600'>{message}</div>
        </div>

        <div
          className={`flex space-x-3 ${buttonsAlign === 'left' ? 'justify-start' : 'justify-end'}`}
        >
          <Button variant='outline' onClick={onClose} disabled={isLoading} className='px-4 py-2'>
            {cancelText}
          </Button>
          <Button
            variant={variantStyles.confirmButtonVariant}
            onClick={handleConfirm}
            disabled={isLoading}
            isLoading={isLoading}
            className='px-4 py-2'
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmModal;
